import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

export interface GatewayHealthStatus {
  awsIotHealthy?: boolean;
  meshHealthy?: boolean;
  internetHealthy?: boolean;
}

@Component({
  selector: 'app-gateway-health-status',
  templateUrl: './gateway-health-status.component.html',
  styleUrl: './gateway-health-status.component.scss',
  imports: [NgClass, MatTooltip],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GatewayHealthStatusComponent {
  readonly gatewayHealth = input<GatewayHealthStatus | null>();
  readonly primary = input(false);
  readonly dataCy = input<string>();
  readonly dataTest = input<string>();

  readonly healthStatusClasses = computed((): string[] => {
    const gatewayHealth = this.gatewayHealth();
    if (!this.hasGatewayHealthDimensions(gatewayHealth)) {
      return ['health-dot-unknown', 'health-ring-unknown'];
    }

    const { awsIotHealthy, meshHealthy, internetHealthy } = gatewayHealth;

    if (awsIotHealthy && meshHealthy && internetHealthy) {
      return ['health-dot-green', 'health-ring-green'];
    }
    if (awsIotHealthy && !meshHealthy && internetHealthy) {
      return ['health-dot-red', 'health-ring-green'];
    }
    if (!awsIotHealthy && internetHealthy) {
      return ['health-dot-red', 'health-ring-yellow'];
    }
    if (awsIotHealthy && meshHealthy && !internetHealthy) {
      return ['health-dot-green', 'health-ring-yellow'];
    }
    if (awsIotHealthy && !meshHealthy && !internetHealthy) {
      return ['health-dot-red', 'health-ring-orange'];
    }
    if (!awsIotHealthy && !internetHealthy) {
      return ['health-dot-red', 'health-ring-red'];
    }

    return ['health-dot-unknown', 'health-ring-unknown'];
  });

  readonly healthStatusTitle = computed((): string => {
    const gatewayHealth = this.gatewayHealth();
    if (!this.hasGatewayHealthDimensions(gatewayHealth)) {
      return 'The health of this gateway is currently unknown.';
    }

    const { awsIotHealthy, meshHealthy, internetHealthy } = gatewayHealth;

    if (awsIotHealthy && meshHealthy && internetHealthy) {
      return 'AWS IoT, LE Mesh and Update Service are available.';
    }
    if (!awsIotHealthy && !meshHealthy && !internetHealthy) {
      return 'AWS IoT, LE Mesh and Update Service are unavailable.';
    }
    if (!internetHealthy && awsIotHealthy && meshHealthy) {
      return 'AWS IoT and LE Mesh are available. Update Service is unavailable.';
    }
    if (awsIotHealthy && !meshHealthy && internetHealthy) {
      return 'AWS IoT and Update Service are available. LE Mesh - no data.';
    }
    if (!awsIotHealthy && meshHealthy && internetHealthy) {
      return 'LE Mesh and Update Service are available. AWS IoT is unavailable.';
    }
    if (awsIotHealthy && !meshHealthy && !internetHealthy) {
      return 'AWS IoT is available. LE Mesh and Update Service are unavailable.';
    }
    if (!awsIotHealthy && meshHealthy && !internetHealthy) {
      return 'LE Mesh is available. AWS IoT and Update Service are unavailable.';
    }

    return 'Update Service is available. AWS IoT and LE Mesh connections are unavailable.';
  });

  private hasGatewayHealthDimensions(health?: GatewayHealthStatus | null): health is Required<GatewayHealthStatus> {
    return (
      health?.awsIotHealthy !== undefined &&
      health.meshHealthy !== undefined &&
      health.internetHealthy !== undefined
    );
  }
}
